import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../shared/api/axios';
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import StartFooter from "./StartFooter";


type HandleStatus =
  | 'idle'
  | 'checking'
  | 'unused'
  | 'used'
  | 'too_short'
  | 'invalid_chars'
  | 'error';

  type EmailStatus =
  | 'idle'
  | 'checking'
  | 'valid'
  | 'invalid'
  | 'used'
  | 'unused'
  | 'error';

const ProfileSetup = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [profilename, setProfilename] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isOver18, setIsOver18] = useState(false);
  const [loading, setLoading] = useState(false);

  //For Email check
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const emailDebTimer = useRef<number | null>(null);
  const lastEmailQueried = useRef<string>('');


  // --- Handle availability (kept) ---
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle');
  const debTimer = useRef<number | null>(null);
  const lastQueried = useRef<string>('');
  const tx = (key: string, def: string) => t(key, { defaultValue: def });

  useEffect(() => {
    const trimmed = profilename.trim();
    if (!trimmed) {
      setHandleStatus('idle');
      return;
    }
    if (trimmed.length < 3) {
      setHandleStatus('too_short');
      return;
    }
    if (!/^[A-Za-z0-9_-]{3,18}$/.test(trimmed)) {
      setHandleStatus('invalid_chars');
      return;
    }

    setHandleStatus('checking');
    if (debTimer.current) window.clearTimeout(debTimer.current);
    debTimer.current = window.setTimeout(async () => {
      try {
        lastQueried.current = trimmed;
        const { data } = await api.get('/users/check-handle', {
          params: { profilename: lastQueried.current },
        });
        if (lastQueried.current === trimmed) {
          setHandleStatus(
            data?.status === 'unused'
              ? 'unused'
              : data?.status === 'used'
              ? 'used'
              : data?.status === 'too_short'
              ? 'too_short'
              : data?.status === 'invalid_chars'
              ? 'invalid_chars'
              : 'error'
          );
        }
      } catch {
        if (lastQueried.current === trimmed) setHandleStatus('error');
      }
    }, 350) as unknown as number;

    return () => {
      if (debTimer.current) window.clearTimeout(debTimer.current);
    };
  }, [profilename]);



  // --- Email: ONLY format check (no availability ping) ---
  const emailTrim = email.trim().toLowerCase();

useEffect(() => {
  if (!emailTrim) {
    setEmailStatus('idle');
    return;
  }

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);

  if (!emailOk) {
    setEmailStatus('invalid');
    return;
  }

  setEmailStatus('checking');

  if (emailDebTimer.current) window.clearTimeout(emailDebTimer.current);

  emailDebTimer.current = window.setTimeout(async () => {
    try {
      lastEmailQueried.current = emailTrim;

      const { data } = await api.get('/users/check-email', {
        params: { email: lastEmailQueried.current },
      });

      if (lastEmailQueried.current === emailTrim) {
        setEmailStatus(
          data?.status === 'unused'
            ? 'unused'
            : data?.status === 'used'
            ? 'used'
            : data?.status === 'invalid'
            ? 'invalid'
            : 'error'
        );
      }
    } catch {
      if (lastEmailQueried.current === emailTrim) setEmailStatus('error');
    }
  }, 350) as unknown as number;

  return () => {
    if (emailDebTimer.current) window.clearTimeout(emailDebTimer.current);
  };
}, [emailTrim]);


  // --- Password policy ---
  const pw = password || '';
  const pwChecks = {
    len: pw.length >= 8,
    digit: /\d/.test(pw),
  };
  const pwValid = pwChecks.len && pwChecks.digit;

  const canSubmit =
  !!profilename &&
  handleStatus === 'unused' &&
  emailStatus === 'unused' &&
  pwValid &&
  isOver18 &&
  !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      await api.post('/auth/register', {
        profilename: profilename.trim(),
        email: emailTrim.toLowerCase(),
        password,
        language: i18n.language,
      });
      sessionStorage.setItem('registrationFlow', 'normal');
sessionStorage.setItem('registrationReturnTo', '/gallery');

navigate('/check-email', {
  state: {
    email: emailTrim,
    flow: 'normal',
    returnTo: '/gallery',
  },
});
    } catch (err: any) {
      console.error('❌ Registration error:', err);
      const msg =
        err?.response?.data?.msg ??
        t('profileSetup.registerError') ??
        '⚠️ Registration failed. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (

       <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background:
          'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
        color: '#f9fafb',
        fontFamily:
          'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >


 <div style={floatingLogoStyle}>
  <button
    type="button"
    onClick={() => navigate('/')}
    aria-label="Go to homepage"
    style={floatingLogoButtonStyle}
  >
    <HighLogo
      src={logo}
      style={{
        width: 190,
        display: 'block',
      }}
      padding={0}
    />
  </button>
</div>


 <main style={mainStyle}>
 <section style={signupPanel}>
  <button
    type="button"
    onClick={() => navigate(-1)}
    aria-label="Back"
    style={backBtn}
  >
    ‹
  </button>

  <div style={titleWrap}>
    <h1 style={titleStyle}>
      {t("profileSetup.title") || "Create your profile"}
    </h1>
  </div>

  <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroup}>
            <label htmlFor="profilename" style={labelStyle}>
              {t("profileSetup.profileName")}
            </label>
            <input
              id="profilename"
              type="text"
              value={profilename}
              onChange={(e) => setProfilename(e.target.value)}
              placeholder= {t("profileSetup.profileName")}
              style={inputModern}
              required
            />

            <div style={statusLine}>
              {handleStatus === "unused" && (
                <span style={{ color: "#22c55e" }}>✓ Profile name available</span>
              )}
              {handleStatus === "used" && (
                <span style={{ color: "#f97373" }}>✕ This name is already taken</span>
              )}
              {handleStatus === "too_short" && (
                <span style={{ color: "#f97373" }}>✕ Too short. Minimum 3 characters.</span>
              )}
              {handleStatus === "invalid_chars" && (
                <span style={{ color: "#f97373" }}>✕ Only letters, numbers, _ and -</span>
              )}
              {handleStatus === "checking" && (
                <span style={{ color: "#9ca3af" }}>Checking...</span>
              )}
            </div>
          </div>

          <div style={fieldGroup}>
            <label htmlFor="email" style={labelStyle}>
              {t("profileSetup.email")}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("profileSetup.emailplace")}
              style={inputModern}
              required
            />
                      <div style={statusLine}>
  {emailStatus === "unused" && (
    <span style={{ color: "#22c55e" }}>✓ Email available</span>
  )}
  {emailStatus === "used" && (
    <span style={{ color: "#f97373" }}>✕ This email is already used</span>
  )}
  {emailStatus === "invalid" && (
    <span style={{ color: "#f97373" }}>✕ Invalid email address</span>
  )}
  {emailStatus === "checking" && (
    <span style={{ color: "#9ca3af" }}>Checking...</span>
  )}
</div>

          </div>



          <div style={fieldGroup}>
            <label htmlFor="password" style={labelStyle}>
              {t("profileSetup.password")}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("profileSetup.createpassword")}
              style={inputModern}
              required
            />

            {password.length > 0 && (
              <div style={passwordChecks}>
                <span style={{ color: pwChecks.len ? "#22c55e" : "#f97373" }}>
                  {pwChecks.len ? "✓" : "✕"} At least 8 characters
                </span>
                <span style={{ color: pwChecks.digit ? "#22c55e" : "#f97373" }}>
                  {pwChecks.digit ? "✓" : "✕"} At least one number
                </span>
              </div>
            )}
          </div>

          <label style={ageRow}>
            <input
              id="over18"
              type="checkbox"
              checked={isOver18}
              onChange={() => setIsOver18(!isOver18)}
            />
            <span>{t("profileSetup.over18")}</span>
          </label>

          <button type="submit" disabled={!canSubmit} style={canSubmit ? submitBtn : submitBtnDisabled}>
            {loading
              ? t("profileSetup.creating") || "Creating..."
              : t("profileSetup.createProfile") || "Create Profile"}
          </button>

          <button type="button" onClick={() => navigate("/start")} style={loginLink}>
            {t("profileSetup.alreadyAccount") || "Already have an account? Log in"}
          </button>

          <div style={legalBlock}>
            <div>
              {t(
                "profileSetup.legalNotice",
                "By creating an account, you agree to our policies."
              )}
            </div>

            <span onClick={() => navigate("/legal/privacypolicy")} style={legalLink}>
              {t("profileSetup.privacyPolicy", "Privacy Policy")}
            </span>

            {" · "}

            <span onClick={() => navigate("/legal/termsofuse")} style={legalLink}>
              {t("profileSetup.termsOfUse", "Terms of Use")}
            </span>
          </div>
        </form>
      </section>

      <section style={phoneSide}>
        <div style={phoneFrame}>
          <div style={phoneSpeaker} />

          <div style={phoneScreen}>
            {/* Later replace this block with your video */}
 <img
  src="/images/setup.webp"
  alt="Elnydo profile setup preview"
  style={{
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  }}
/>
          </div>
        </div>
      </section>
    </main>
    <StartFooter />
  </div>
);
};


const mainStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  maxWidth: 1240,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns:
    "minmax(420px, 560px) minmax(280px, 340px)",
  alignItems: "center",
  justifyContent: "center",
  columnGap: "clamp(50px, 7vw, 90px)",
  padding: "38px clamp(24px, 4vw, 48px) 64px",
  boxSizing: "border-box",
};


const signupPanel: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 560,
  padding: "20px 0",
  boxSizing: "border-box",
  transform: "translateX(35px)",
};




const floatingLogoStyle: React.CSSProperties = {
  width: "100%",
  padding: "28px 20px 0",
  boxSizing: "border-box",
};

const floatingLogoButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
};

const backBtn: React.CSSProperties = {
  position: "absolute",
  top: -10,
  left: 20,
  width: 45,
  height: 45,
  borderRadius: "50%",
  background: "transparent",
  border: "none",
  color: "#f8e298",
  cursor: "pointer",
  fontSize: 40,
  lineHeight: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color .2s ease",
};

const titleStyle: React.CSSProperties = {
  fontSize: 32,
  lineHeight: 1.06,
  margin: 0,
  color: "#f8e298",
  letterSpacing: -1.2,
  fontWeight: 800, 
};

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
};

const fieldGroup: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 9,
};

const titleWrap: React.CSSProperties = {
  width: "100%",
  textAlign: "center",
  marginTop: -20,
  marginBottom: 46,
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#94A3B8",
  fontWeight: 600,
  marginLeft: 4,
  marginBottom: 1,
};

const inputModern: React.CSSProperties = {
  width: "100%",
  height: 50,
  padding: "0 18px",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(15,23,42,0.45)",
  color: "#f8fafc",
  fontSize: 16,
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
};

const statusLine: React.CSSProperties = {
  minHeight: 16,
  fontSize: 13,
};

const passwordChecks: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  fontSize: 13,
  marginTop: 2,
};

const ageRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 15,
  color: "#cbd5e1",
  marginTop: 2,
};

const submitBtn: React.CSSProperties = {
  marginTop: 4,
  padding: "12px 18px",
  borderRadius: 999,
  border: "1px solid rgba(250,250,250,0.22)",
  background:
    "linear-gradient(135deg, rgba(212,175,55,0.98), rgba(250,250,210,0.92))",
  color: "#111827",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
};

const submitBtnDisabled: React.CSSProperties = {
  ...submitBtn,
  background: "rgba(75,85,99,0.65)",
  color: "#cbd5e1",
  cursor: "not-allowed",
};

const loginLink: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#93c5fd",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 13,
  marginTop: 2,
};

const legalBlock: React.CSSProperties = {
  marginTop: 6,
  textAlign: "center",
  fontSize: 10.5,
  color: "#64748b",
  lineHeight: 1.5,
};

const legalLink: React.CSSProperties = {
  cursor: "pointer",
  color: "#94a3b8",
};

const phoneSide: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const phoneFrame: React.CSSProperties = {
  width: 285,
  height: 585,
  borderRadius: 42,
  padding: 12,
  background:
    "linear-gradient(145deg, #111827, #020617)",
  border: "1px solid rgba(248,250,252,0.18)",
  boxShadow:
    "0 35px 90px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.08)",
  position: "relative",
};

const phoneSpeaker: React.CSSProperties = {
  position: "absolute",
  top: 18,
  left: "50%",
  transform: "translateX(-50%)",
  width: 78,
  height: 6,
  borderRadius: 999,
  background: "rgba(148,163,184,0.45)",
  zIndex: 2,
};

const phoneScreen: React.CSSProperties = {
  width: "100%",
  height: "100%",
  borderRadius: 32,
  overflow: "hidden",
  background:
    "radial-gradient(circle at top, rgba(212,175,55,0.28), rgba(15,23,42,1) 40%, #000 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default ProfileSetup;
